import { AppLayout } from "@/components/layout/AppLayout";
import { useDecisionMetrics, VehicleDecisionData } from "@/hooks/useDecisionMetrics";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, ArrowRight, Sparkles } from "lucide-react";
import { useState, useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const pct = (n: number) => `${Math.round(n)}%`;

interface ComparisonRow {
  label: string;
  a: string;
  b: string;
  winner: "a" | "b" | "tie";
  annotation?: string;
}

function buildComparison(a: VehicleDecisionData, b: VehicleDecisionData): ComparisonRow[] {
  const rows: ComparisonRow[] = [];
  const nameA = a.vehicle.name || `${a.vehicle.year} ${a.vehicle.make} ${a.vehicle.model}`;
  const nameB = b.vehicle.name || `${b.vehicle.year} ${b.vehicle.make} ${b.vehicle.model}`;

  rows.push({
    label: "Profit / month",
    a: fmt(a.monthlyProfit),
    b: fmt(b.monthlyProfit),
    winner: a.monthlyProfit > b.monthlyProfit ? "a" : a.monthlyProfit < b.monthlyProfit ? "b" : "tie",
    annotation:
      Math.abs(a.monthlyProfit - b.monthlyProfit) > 50
        ? `${a.monthlyProfit > b.monthlyProfit ? nameA : nameB} earns ${fmt(Math.abs(a.monthlyProfit - b.monthlyProfit))} more per month`
        : undefined,
  });

  rows.push({
    label: "Expense ratio",
    a: pct(a.expenseRatio),
    b: pct(b.expenseRatio),
    winner: a.expenseRatio < b.expenseRatio ? "a" : a.expenseRatio > b.expenseRatio ? "b" : "tie",
    annotation:
      Math.abs(a.expenseRatio - b.expenseRatio) > 10
        ? `${a.expenseRatio > b.expenseRatio ? nameA : nameB} costs more relative to earnings`
        : undefined,
  });

  rows.push({
    label: "Utilization",
    a: pct(a.utilization),
    b: pct(b.utilization),
    winner: a.utilization > b.utilization ? "a" : a.utilization < b.utilization ? "b" : "tie",
  });

  rows.push({
    label: "Maintenance tasks",
    a: String(a.maintenanceCount),
    b: String(b.maintenanceCount),
    winner: a.maintenanceCount < b.maintenanceCount ? "a" : a.maintenanceCount > b.maintenanceCount ? "b" : "tie",
    annotation:
      Math.abs(a.maintenanceCount - b.maintenanceCount) > 2
        ? `${a.maintenanceCount > b.maintenanceCount ? nameA : nameB} requires more frequent maintenance`
        : undefined,
  });

  return rows;
}

const VehicleComparisons = () => {
  const { vehicleData, isLoading } = useDecisionMetrics();
  const [vehicleA, setVehicleA] = useState<string>("");
  const [vehicleB, setVehicleB] = useState<string>("");

  // Auto-suggested comparisons
  const suggestions = useMemo(() => {
    if (vehicleData.length < 2) return [];
    const sorted = [...vehicleData].sort((a, b) => b.monthlyProfit - a.monthlyProfit);
    const result: { label: string; a: string; b: string }[] = [];

    if (sorted.length >= 2) {
      result.push({
        label: "Highest vs lowest profit",
        a: sorted[0].vehicle.id,
        b: sorted[sorted.length - 1].vehicle.id,
      });
    }

    const byDate = [...vehicleData].sort(
      (a, b) => a.vehicle.year - b.vehicle.year
    );
    if (byDate.length >= 2 && byDate[0].vehicle.id !== byDate[byDate.length - 1].vehicle.id) {
      result.push({
        label: "Newest vs oldest vehicle",
        a: byDate[byDate.length - 1].vehicle.id,
        b: byDate[0].vehicle.id,
      });
    }

    return result;
  }, [vehicleData]);

  const dataA = vehicleData.find((v) => v.vehicle.id === vehicleA);
  const dataB = vehicleData.find((v) => v.vehicle.id === vehicleB);
  const comparison = dataA && dataB ? buildComparison(dataA, dataB) : null;
  const nameA = dataA ? dataA.vehicle.name || `${dataA.vehicle.year} ${dataA.vehicle.make} ${dataA.vehicle.model}` : "";
  const nameB = dataB ? dataB.vehicle.name || `${dataB.vehicle.year} ${dataB.vehicle.make} ${dataB.vehicle.model}` : "";

  const getName = (id: string) => {
    const v = vehicleData.find((d) => d.vehicle.id === id);
    if (!v) return "";
    return v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Vehicle Comparisons</h1>
          <p className="text-muted-foreground mt-1">
            Compare two vehicles side by side. Focus on differences that matter, not just numbers.
          </p>
        </div>

        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : vehicleData.length < 2 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <BarChart3 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">Need at least two vehicles</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add more vehicles with trip data to start comparing.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Suggested comparisons */}
            {suggestions.length > 0 && !vehicleA && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Suggested</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <Button
                      key={s.label}
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        setVehicleA(s.a);
                        setVehicleB(s.b);
                      }}
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Selectors */}
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={vehicleA} onValueChange={setVehicleA}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Select vehicle A" />
                </SelectTrigger>
                <SelectContent>
                  {vehicleData.map((v) => (
                    <SelectItem key={v.vehicle.id} value={v.vehicle.id} disabled={v.vehicle.id === vehicleB}>
                      {v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />

              <Select value={vehicleB} onValueChange={setVehicleB}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Select vehicle B" />
                </SelectTrigger>
                <SelectContent>
                  {vehicleData.map((v) => (
                    <SelectItem key={v.vehicle.id} value={v.vehicle.id} disabled={v.vehicle.id === vehicleA}>
                      {v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(vehicleA || vehicleB) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setVehicleA("");
                    setVehicleB("");
                  }}
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Comparison table */}
            {comparison && (
              <Card>
                <CardContent className="p-0">
                  {/* Header */}
                  <div className="grid grid-cols-3 gap-4 p-4 border-b bg-muted/30">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Metric</div>
                    <div className="text-sm font-semibold text-foreground truncate">{nameA}</div>
                    <div className="text-sm font-semibold text-foreground truncate">{nameB}</div>
                  </div>

                  {comparison.map((row, i) => (
                    <div key={row.label}>
                      <div className="grid grid-cols-3 gap-4 p-4 items-center">
                        <div className="text-sm text-muted-foreground">{row.label}</div>
                        <div className={`text-sm font-medium ${row.winner === "a" ? "text-primary" : "text-foreground"}`}>
                          {row.a}
                          {row.winner === "a" && <span className="text-xs ml-1.5">✦</span>}
                        </div>
                        <div className={`text-sm font-medium ${row.winner === "b" ? "text-primary" : "text-foreground"}`}>
                          {row.b}
                          {row.winner === "b" && <span className="text-xs ml-1.5">✦</span>}
                        </div>
                      </div>
                      {row.annotation && (
                        <div className="px-4 pb-3 -mt-1">
                          <p className="text-xs text-muted-foreground italic">{row.annotation}</p>
                        </div>
                      )}
                      {i < comparison.length - 1 && <div className="border-b" />}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default VehicleComparisons;
